// 阿里云 ARMS RUM 前端监控
// 注意：@arms/rum-browser 在模块求值时即引用 window，必须仅在浏览器端动态加载，
// 否则会在 Next.js 服务端预渲染阶段抛出 "window is not defined"。

type ArmsRumInstance = (typeof import('@arms/rum-browser'))['default'];

let armsRumPromise: Promise<ArmsRumInstance> | null = null;
// 初始化（含 await init()）的共享 Promise，保证只初始化一次，且其它操作（如 setConfig('user')）
// 可 await 它以确保在 init 完成之后再执行，避免被 init 的配置覆盖。
let initPromise: Promise<ArmsRumInstance | null> | null = null;

// 与构建版本保持一致，用于 ARMS 控制台按版本匹配 SourceMap 还原堆栈
const APP_VERSION = process.env.NEXT_PUBLIC_BUILD_ID;

function loadArmsRum(): Promise<ArmsRumInstance> | null {
  if (typeof window === 'undefined') return null;
  if (!armsRumPromise) {
    armsRumPromise = import('@arms/rum-browser').then(m => m.default);
  }
  return armsRumPromise;
}

// 初始化，仅在浏览器端执行一次。返回一个会在 init() 完成后 resolve 的共享 Promise，
// 供 setArmsUser 等操作 await，确保配置在初始化之后生效。
export function initArmsRum(): Promise<ArmsRumInstance | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!initPromise) {
    initPromise = (async () => {
      const armsRum = await loadArmsRum();
      if (!armsRum) return null;
      // 注意：必须 await init()，否则后续 setConfig 可能在初始化完成前执行而被覆盖
      await armsRum.init({
        endpoint: 'https://proj-xtrace-1cf1ec99ef55e2ada386ea6f5cd7f36-us-west-1.us-west-1.log.aliyuncs.com/rum/web/v2?workspace=default-cms-1065825344740335-us-west-1&service_id=ahsxejgoof@28778b107c80573f1aa55',
        // 根据构建环境区分上报环境
        env: process.env.NODE_ENV === 'production' ? 'prod' : 'local',
        // 应用版本号，需与上传的 SourceMap 版本一致才能正确还原堆栈
        version: APP_VERSION,
        // SPA 路由模式
        spaMode: 'history',
        // 仅保留错误 + Web Vitals 采集，关闭性能/资源/接口/行为采集以降低 SLS 写入量与成本。
        // 错误排查所需的 event_type: exception 全部来自 jsError / consoleError。
        // webVitals（CLS/INP/LCP 等，event_type: web_vitals）用于自查 GSC/CrUX 报告的
        // Core Web Vitals 问题（如 CLS>0.1、INP>200ms），量小（每 PV 若干条）成本可控。
        collectors: {
          perf: false,
          webVitals: true,
          api: false,
          staticResource: false,
          jsError: true,
          consoleError: true,
          action: false,
        },
        tracing: false,
      });
      return armsRum;
    })();
  }
  return initPromise;
}

// 是否已尝试初始化 ARMS。注意：ARMS SDK 一旦 init 完成，collector/reporter 即已注册，
// 没有运行时停止接口（setConfig('enable', false) 仅在 init 阶段生效）。
// 因此撤回同意时需配合刷新页面，刷新后因未同意而不再初始化，才能彻底停止上报。
export function isArmsInitialized() {
  return initPromise != null;
}

// 关联登录用户，便于在控制台按用户排查错误。
// 必须等 init 完成后再 setConfig('user')，否则会被初始化配置覆盖导致 user.id 设置不上。
export async function setArmsUser(id?: string | number | null) {
  const armsRum = await initArmsRum();
  if (!armsRum) return;
  armsRum.setConfig('user', { id: id != null ? String(id) : '' });
}

// 手动上报错误（用于 React 错误边界等 SDK 无法自动捕获的场景）
export async function reportError(
  error: Error & { digest?: string },
  extra?: Record<string, string | number | boolean | null | undefined>,
) {
  const armsRum = await loadArmsRum();
  if (!armsRum) return;
  armsRum.sendException({
    name: error.name,
    message: error.message,
    stack: error.stack,
    type: 'error',
    source: 'react-error-boundary',
    properties: {
      digest: error.digest,
      url: window.location.href,
      ...extra,
    },
  });
}
